/**
 * The desktop client, seen from inside the web client.
 *
 * `app-electron/` runs this same client inside a window and puts a very small
 * object on `window` so the two can cooperate on the things a page cannot do
 * for itself: a count on the taskbar icon, and bringing the window forward
 * when somebody clicks a notification.
 *
 * It is read defensively. The bridge is injected by whatever is hosting the
 * page, and a browser extension can put an object of that name on `window`
 * just as easily, so nothing is trusted beyond "these two are functions" — and
 * nothing is ever read back out of it. In an ordinary browser it is simply
 * absent, and every caller here is a no-op.
 */

export interface UnreadReport {
  /** Unread messages across every conversation. */
  unread: number
  /** Of those, the ones that named the reader. */
  mentions: number
}

export interface DesktopBridge {
  platform: 'electron'
  version: string
  setUnread(report: UnreadReport): void
  focus(): void
}

function looksLikeBridge(value: unknown): value is DesktopBridge {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<DesktopBridge>
  return (
    candidate.platform === 'electron' &&
    typeof candidate.setUnread === 'function' &&
    typeof candidate.focus === 'function'
  )
}

export function desktopBridge(): DesktopBridge | null {
  if (typeof window === 'undefined') return null
  const candidate = (window as { relaiiDesktop?: unknown }).relaiiDesktop
  return looksLikeBridge(candidate) ? candidate : null
}

/** True when this page is running inside the relaii desktop client. */
export function isDesktop(): boolean {
  return desktopBridge() !== null
}

/** Tells the host what to put on its badge. Silent in a browser. */
export function reportUnread(report: UnreadReport): void {
  try {
    desktopBridge()?.setUnread(report)
  } catch {
    // A host that throws is a host that does not get told again this tick.
    // It must never take the conversation down with it.
  }
}

/** Asks the host to bring its window forward. Silent in a browser. */
export function focusDesktopWindow(): void {
  try {
    desktopBridge()?.focus()
  } catch {
    // As above.
  }
}
